import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { productApiClient } from '../clients/ProductAPIClient';
import { masterAPIClient } from '../clients/MasterAPIClient';
import { ILoadAlbum, loadAlbum } from '../state/actions/albumAction';
import { duplicateAlbum } from '../helpers/AlbumHelper';
import { logger } from '../utils/Logger';
import { ICreateAlbum } from '../data/models/createAlbum';
import { IProductList } from '../data/models/productList';
import { IOptions } from '../data/models/option';
import { IFormat } from '@/data/models/format';
import { IAlbum } from '../data/models/album';
import { FormatType } from '@/data/models/formatType';
import { translateMerchandiseProductType } from '@/helpers/translateProductType';

export const useCreateProduct = (
  togglePopUp: any,
  albumToCopy?: IProductList,
  formatType?: FormatType
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [album, setAlbum] = useState<ICreateAlbum>({
    title: albumToCopy ? albumToCopy.title : '',
    label: {
      name: '',
      adminOrgId: '',
    },
    format: {
      id: 0,
      name: '',
    },
    albumMetaData: {
      metadataLanguage: 'en',
    },
  });
  const [showPopUp, setShowPopUp] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formats, setFormats] = useState<IOptions<IFormat>[]>([]);
  const isSubmittingRef = useRef(false);

  const handleInput = (field: string, value: any) => {
    setSubmitError(null);
    setAlbum({ ...album, [field]: value });
  };

  const handleFormat = (selectedOption: IOptions<IFormat>) => {
    setSubmitError(null);
    setAlbum({ ...album, format: selectedOption.value });
  };

  const handleLabelSelect = (selectedOption: any) => {
    setSubmitError(null);
    const updatedAlbum = {
      ...album,
      label: {
        name: '',
        adminOrgId: selectedOption ? selectedOption.value : '',
      },
    };
    setAlbum(updatedAlbum);
  };

  const convertOptionToFormat = (option?: IOptions<IFormat>) => {
    if (!option) return option;
    return option.value;
  };

  const getFormats = () => {
    masterAPIClient
      .getFormat(formatType)
      .then((data) => {
        setFormats(data);
        if (albumToCopy) {
          const albumFormat = convertOptionToFormat(
            data.find((c) => c.value.id === albumToCopy.format.id)
          );
          if (!albumFormat) return;
          setAlbum((prev) => ({ ...prev, format: albumFormat }));
        }
      })
      .catch((result: any) => {
        logger.error(result);
      });
  };

  useEffect(() => {
    const isMerchandiseTypeSelected =
      formatType === FormatType.Merchandise
        ? !!(album as any).merchandiseMetadata?.merchandiseType
        : true;

    if (
      (album.label.adminOrgId || albumToCopy) &&
      album.title &&
      album.format?.id &&
      isMerchandiseTypeSelected
    ) {
      setIsSaveDisabled(false);
    } else {
      setIsSaveDisabled(true);
    }
  }, [album, albumToCopy, formatType]);

  useEffect(() => {
    getFormats();
  }, []);

  const loadOptions = (inputText: any, callback: any) => {
    productApiClient.getLabel({ value: inputText }).then((result) => {
      callback(result);
    });
  };

  const loadMerchandiseTypes = async (
    inputValue: string,
    callback: (options: any[]) => void,
    formatId?: number
  ) => {
    if (!formatId) {
      callback?.([]);
      return [];
    }

    try {
      const types = await masterAPIClient.getMerchandiseTypes(formatId);
      const translatedTypes = types.map((mType) => {
        return {
          ...mType,
          label: translateMerchandiseProductType(mType.label),
        };
      });
      const filteredTypes = inputValue
        ? translatedTypes.filter((t: any) =>
            t.label.toLowerCase().includes(inputValue.toLowerCase())
          )
        : translatedTypes;
      callback?.(filteredTypes);
      return filteredTypes;
    } catch (error: any) {
      logger.error(error);
      callback?.([]);
      return [];
    }
  };

  const handleMerchandiseType = (selectedOption: any) => {
    setSubmitError(null);
    setAlbum((prev: any) => ({
      ...prev,
      merchandiseMetadata: {
        ...prev.merchandiseMetadata,
        merchandiseType: selectedOption ? selectedOption.value : null,
      },
    }));
  };

  const getSubmitErrorMessage = (error: any) => {
    const responseData = error?.response?.data;

    if (typeof responseData === 'string') {
      return responseData;
    }

    if (responseData?.message) {
      return responseData.message;
    }

    if (responseData?.title) {
      return responseData.title;
    }

    return t('product.createProduct.errorMessage');
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let albumToCreate = {};
      if (albumToCopy) {
        const originalAlbum: IAlbum = (
          await productApiClient.getAlbum(albumToCopy!.id.toString())
        ).data;
        const newAlbum = duplicateAlbum(
          originalAlbum,
          album.title,
          album.format
        );
        albumToCreate = newAlbum;
      } else {
        albumToCreate = album;
      }

      const response = await productApiClient.createProduct(albumToCreate);
      const id = response?.data.id || '';
      const loadAlbumValues: ILoadAlbum = {
        isJustCreated: true,
      };
      dispatch(loadAlbum(loadAlbumValues));
      setShowPopUp(false);
      togglePopUp();
      formatType === FormatType.Merchandise
        ? navigate(`/Merchandise/CreateProduct/${id}`)
        : navigate(`/Physical/CreateProduct/Album/${id}`);
    } catch (error: any) {
      logger.error(error);
      setSubmitError(getSubmitErrorMessage(error));
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const closePopUp = () => {
    setShowPopUp(false);
    togglePopUp();
  };

  return {
    t,
    album,
    showPopUp,
    isSaveDisabled,
    isSubmitting,
    submitError,
    formats,
    handleInput,
    handleFormat,
    handleLabelSelect,
    loadOptions,
    handleSubmit,
    closePopUp,
    loadMerchandiseTypes,
    handleMerchandiseType,
  };
};
